import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface VolcesJimengI2i30Params {
  /**
   * 图像生成提示词，长度必须在1-800字符之间
   */
  prompt: string;
  /**
   * 输入图像的Base64编码列表，必须包含 exactly one 图像
   */
  binary_data_base64?: string[];
  /**
   * 输入图像URL列表，必须包含 exactly one 图像
   */
  image_urls?: string[];
  /**
   * 随机种子，必须是-1或正整数
   * @default -1
   */
  seed?: number;
  /**
   * 缩放因子，必须是0到1之间的浮点数
   * @default 0.5
   */
  scale?: number;
  /**
   * 图像宽度，必须在512-2016之间，需要与height同时提供
   */
  width?: number;
  /**
   * 图像高度，必须在512-2016之间，需要与width同时提供
   */
  height?: number;
}

export interface VolcesJimengI2i30Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_jimeng_i2i_3_0
 *
 * Volces Jimeng I2I 3.0 Image Generator
 * 
 * Image-to-image generation implementation for Volces Jimeng I2I 3.0 (火山即梦图生图3.0).
 * API Documentation: https://www.volcengine.com/docs/85621/1747301
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesJimengI2i30 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesJimengI2i30({
 *   // 参数见 VolcesJimengI2i30Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesJimengI2i30Params 接口定义
 * @returns 生成的资源列表
 */
export async function volcesJimengI2i30(
  params: VolcesJimengI2i30Params,
): Promise<VolcesJimengI2i30Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_jimeng_i2i_3_0',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: VolcesJimengI2i30Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
